import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useActiveLease } from '@/hooks/useLeases';
import { useSubmitReading } from '@/hooks/useUtilities';
import { type UtilityType } from '@/api/utilities';

interface FormData {
  type: UtilityType;
  value: string;
}

export function UtilitiesSubmitPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeLease } = useActiveLease();
  const submit = useSubmitReading();
  const [photo, setPhoto] = useState<File | null>(null);
  const [receipt, setReceipt] = useState<File | null>(null);
  const { register, handleSubmit } = useForm<FormData>();

  const onSubmit = handleSubmit(async (data) => {
    if (!activeLease) return;
    const form = new FormData();
    form.append('lease_id', String(activeLease.id));
    form.append('type', data.type);
    form.append('value', data.value);
    const now = new Date();
    form.append('period_month', String(now.getMonth() + 1));
    form.append('period_year', String(now.getFullYear()));
    if (photo) form.append('photo', photo);
    if (receipt) form.append('receipt', receipt);
    await submit.mutateAsync(form);
    navigate('/utilities');
  });

  return (
    <Card title={t('utilities.submit_reading')}>
      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block text-sm font-medium text-slate-700">
          {t('utilities.type')}
          <select {...register('type')} className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="electricity">{t('utilities.types.electricity')}</option>
            <option value="water_cold">{t('utilities.types.water_cold')}</option>
            <option value="water_hot">{t('utilities.types.water_hot')}</option>
            <option value="gas">{t('utilities.types.gas')}</option>
            <option value="heating">{t('utilities.types.heating')}</option>
          </select>
        </label>
        <Input
          label={t('utilities.value')}
          type="number"
          step="0.01"
          {...register('value', { required: true })}
        />
        <label className="block text-sm font-medium text-slate-700">
          {t('utilities.meter_photo')}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
            className="mt-1 block w-full text-sm"
          />
        </label>
        <label className="block text-sm font-medium text-slate-700">
          {t('utilities.receipt_photo')}
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => setReceipt(e.target.files?.[0] ?? null)}
            className="mt-1 block w-full text-sm"
          />
        </label>
        <Button type="submit" className="w-full" loading={submit.isPending}>
          {t('common.submit')}
        </Button>
      </form>
    </Card>
  );
}
